import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { FaMoneyBillWave, FaMapMarkerAlt, FaGift, FaBox, FaPlus, FaEdit, FaTrash, FaHandshake, FaTools, FaPhoneAlt, FaCloudDownloadAlt, FaStar, FaChartLine, FaWallet, FaBan, FaShareAlt, FaFacebookF, FaInstagram, FaTiktok, FaSnapchatGhost, FaGlobe, FaRobot, FaShieldAlt, FaArrowUp, FaArrowDown, FaTimes } from 'react-icons/fa';
import DropImage from '../components/DropImage';
import '../styles/settings.scss';
// Jaheez social accounts shown in the app side menus (an empty field hides that icon).
const SOCIAL_FIELDS = [
    { key: 'facebook', label: 'فيسبوك', icon: FaFacebookF, placeholder: 'https://www.facebook.com/jaheez' },
    { key: 'instagram', label: 'إنستغرام', icon: FaInstagram, placeholder: 'https://www.instagram.com/jaheez' },
    { key: 'tiktok', label: 'تيك توك', icon: FaTiktok, placeholder: 'https://www.tiktok.com/@jaheez' },
    { key: 'snapchat', label: 'سناب شات', icon: FaSnapchatGhost, placeholder: 'https://www.snapchat.com/add/jaheez' },
    { key: 'website', label: 'الموقع الإلكتروني', icon: FaGlobe, placeholder: 'https://jaheez.ly' },
];
const EMPTY_SOCIALS = { facebook: '', instagram: '', tiktok: '', snapchat: '', website: '' };

const DEFAULT_SIGNUP_TERMS = `1) منصة جاهز وسيط رقمي يربط العميل بالسائق ولا تشغل أو تملك مركبات.
2) العلاقة تعاقدية مباشرة بين العميل والسائق، وكل طرف مسؤول قانونياً عن التزاماته.
3) الشركة غير مسؤولة عن فقدان أو سرقة المركبة أو المقتنيات أو أضرار ميكانيكية/كهربائية تحدث أثناء النقل.
4) السائق يقر بامتلاك رخصة قيادة ورخصة مزاولة سارية ويتحمل أي مسؤولية قانونية أو جزائية.
5) العميل يقر بملكية المركبة أو تفويض قانوني، وألا يستخدم التطبيق لأغراض مخالفة للقانون.
6) يحظر نقل مركبات مسروقة، أو مواد محظورة، أو التحايل على نقاط التفتيش، أو أي نشاط يخل بالأمن.
7) لا تتحمل الشركة مسؤولية النزاعات المالية بين العميل والسائق، وفي حال أي تعويض لا يتجاوز الحد قيمة الرسوم أو العمولة للعملية محل النزاع.
8) يلتزم المستخدم بتعويض الشركة عن أي أضرار أو دعاوى تنشأ عن استخدامه للتطبيق بالمخالفة للقانون.
9) يحق للشركة جمع البيانات اللازمة للتشغيل وفق الخصوصية، والإفصاح للجهات المختصة إذا طُلب رسمياً.
10) يحق للشركة إيقاف أو إلغاء الحساب أو تعديل الشروط في أي وقت.
11) تختص المحاكم الليبية بأي نزاع وينطبق القانون الليبي.`;

export const Settings = () => {
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const {
        settings,
        packages,
        isLoading,
        updatePriceRanges,
        updateMaxSearchRange,
        updateReferralPrize,
        updateJoiningPrize,
        updateDriverSearchConfig,
        updateDriverActivityConfig,
        updateBiddingDisplayConfig,
        updatePayPerTripConfig,
        updateCancellationBanConfig,
        updateEmergencyNumbers,
        updateAppUpdateConfig,
        updateAppTermsConfig,
        updateSocialLinks,
        updateJaheezPolicy,
        createPackage,
        updatePackage,
        deletePackage
    } = useSettings();

    const [activeModal, setActiveModal] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    // "سياسة جاهز" editor: separate lists for users and drivers.
    const [policyTab, setPolicyTab] = useState('user');
    const [policyDraft, setPolicyDraft] = useState({ user: [], driver: [] });
    const [policyLoading, setPolicyLoading] = useState(false);

    // Price ranges state
    const [priceRanges, setPriceRanges] = useState([]);

    const [socialLinks, setSocialLinks] = useState(EMPTY_SOCIALS);

    // Emergency numbers state
    const [emergencyNumbers, setEmergencyNumbers] = useState([]);

    // Max search range state
    const [maxSearchRange, setMaxSearchRange] = useState('');

    // Referral prize state
    const [referralPrize, setReferralPrize] = useState('');

    // Joining prize state
    const [joiningPrizeEnabled, setJoiningPrizeEnabled] = useState(false);

    // App update enforcement state
    const [appUpdateConfig, setAppUpdateConfig] = useState({
        forceUpdate: false,
        message: 'يوجد تحديث إجباري للتطبيق. يرجى التحديث للمتابعة.',
        android: { minimumVersion: '', minimumBuild: '', storeUrl: '' },
        ios: { minimumVersion: '', minimumBuild: '', storeUrl: '' },
    });

    // Signup terms configuration (separate user/driver)
    const [appTermsConfig, setAppTermsConfig] = useState({
        userTerms: DEFAULT_SIGNUP_TERMS,
        driverTerms: DEFAULT_SIGNUP_TERMS,
    });

    // Driver search config state
    const [driverSearchConfig, setDriverSearchConfig] = useState({
        initialDriverBatch: 15,
        nextDriverBatch: 5,
        nextDriverDelaySeconds: 20,
    });

    // Driver activity scoring / leveling config state
    const [driverActivityConfig, setDriverActivityConfig] = useState({
        noBidPenalty: -1,
        bidHigherPoints: 0,
        bidEqualPoints: 1,
        bidLowerPoints: 3,
        tripOnTimePoints: 3,
        tripLateWithinHourPoints: 0,
        tripLateOverHourPoints: -2,
        tripCancelPenalty: -3,
        expectedTimePerKmMinutes: 1.8,
        expectedTimeBaseMinutes: 30,
        levels: {
            normal: { minPoints: 0, discountPercent: 0, dispatchBonusKm: 0, levelUpBonus: 0 },
            good: { minPoints: 20, discountPercent: 5, dispatchBonusKm: 2, levelUpBonus: 0 },
            excellent: { minPoints: 50, discountPercent: 10, dispatchBonusKm: 5, levelUpBonus: 0 },
        },
    });

    // "Average bidding price" hint config state
    const [biddingDisplayConfig, setBiddingDisplayConfig] = useState({
        firstBidWindowSeconds: 8,
        randomOffsetMaxUp: 15,
        randomOffsetMaxDown: 15,
        randomOffsetStep: 5,
    });

    // Pay-per-trip (no-subscription) config state
    const [payPerTripConfig, setPayPerTripConfig] = useState({
        enabled: false,
        pricePerTrip: 10,
        maxDebit: 50,
    });

    // Temporary cancellation-ban config state
    const [cancellationBanConfig, setCancellationBanConfig] = useState({
        enabled: true,
        maxMonthlyCancellations: 3,
        banDurationDays: 7,
        forgiveAfterCompletedTrips: 15,
        userCancellationThreshold: 3,
    });

    // Package state
    const [packageForm, setPackageForm] = useState({
        _id: null,
        name: '',
        packageType: 'standard',
        price: '',
        durationInDays: '',
        maxTrips: '',
        isThereDiscount: false,
        priceAfterDiscount: '',
        packageImage: '',
        description: '',
        badgeLabel: '',
        statusText: '',
        statusTone: 'info',
        ctaText: 'اشتراك الان',
        isActive: true,
        minLevel: 'normal'
    });
    const [packageImageFile, setPackageImageFile] = useState([]);

    const socialCount = useMemo(() => Object.values(socialLinks).filter((v) => String(v || '').trim()).length, [socialLinks]);

    const emergencyCount = useMemo(() => emergencyNumbers.filter((n) => n.name && n.phoneNumber).length, [emergencyNumbers]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        if (settings) {
            setPriceRanges(settings.priceRanges || []);
            setMaxSearchRange(settings.maxSearchRangeKm || '');
            setReferralPrize(settings.referralPrize || '');
            setJoiningPrizeEnabled(Boolean(settings.joiningPrizeEnabled));
            const list = Array.isArray(settings.emergencyNumbers) && settings.emergencyNumbers.length > 0
                ? settings.emergencyNumbers
                : [{ name: '', phoneNumber: '' }];
            setEmergencyNumbers(list);
            setSocialLinks({ ...EMPTY_SOCIALS, ...(settings.socialLinks || {}) });
            setAppUpdateConfig({
                forceUpdate: Boolean(settings?.appUpdate?.forceUpdate),
                message: settings?.appUpdate?.message || 'يوجد تحديث إجباري للتطبيق. يرجى التحديث للمتابعة.',
                android: {
                    minimumVersion: settings?.appUpdate?.android?.minimumVersion || '',
                    minimumBuild: settings?.appUpdate?.android?.minimumBuild || '',
                    storeUrl: settings?.appUpdate?.android?.storeUrl || '',
                },
                ios: {
                    minimumVersion: settings?.appUpdate?.ios?.minimumVersion || '',
                    minimumBuild: settings?.appUpdate?.ios?.minimumBuild || '',
                    storeUrl: settings?.appUpdate?.ios?.storeUrl || '',
                },
            });
            setAppTermsConfig({
                userTerms: settings?.appTerms?.userTerms || DEFAULT_SIGNUP_TERMS,
                driverTerms: settings?.appTerms?.driverTerms || DEFAULT_SIGNUP_TERMS,
            });
            setDriverSearchConfig({
                initialDriverBatch: settings?.initialDriverBatch ?? 15,
                nextDriverBatch: settings?.nextDriverBatch ?? 5,
                nextDriverDelaySeconds: settings?.nextDriverDelaySeconds ?? 20,
            });
            const da = settings?.driverActivity || {};
            setDriverActivityConfig({
                noBidPenalty: da.noBidPenalty ?? -1,
                bidHigherPoints: da.bidHigherPoints ?? 0,
                bidEqualPoints: da.bidEqualPoints ?? 1,
                bidLowerPoints: da.bidLowerPoints ?? 3,
                tripOnTimePoints: da.tripOnTimePoints ?? 3,
                tripLateWithinHourPoints: da.tripLateWithinHourPoints ?? 0,
                tripLateOverHourPoints: da.tripLateOverHourPoints ?? -2,
                tripCancelPenalty: da.tripCancelPenalty ?? -3,
                expectedTimePerKmMinutes: da.expectedTimePerKmMinutes ?? 1.8,
                expectedTimeBaseMinutes: da.expectedTimeBaseMinutes ?? 30,
                levels: {
                    normal: {
                        minPoints: da.levels?.normal?.minPoints ?? 0,
                        discountPercent: da.levels?.normal?.discountPercent ?? 0,
                        dispatchBonusKm: da.levels?.normal?.dispatchBonusKm ?? 0,
                        levelUpBonus: da.levels?.normal?.levelUpBonus ?? 0,
                    },
                    good: {
                        minPoints: da.levels?.good?.minPoints ?? 20,
                        discountPercent: da.levels?.good?.discountPercent ?? 5,
                        dispatchBonusKm: da.levels?.good?.dispatchBonusKm ?? 2,
                        levelUpBonus: da.levels?.good?.levelUpBonus ?? 0,
                    },
                    excellent: {
                        minPoints: da.levels?.excellent?.minPoints ?? 50,
                        discountPercent: da.levels?.excellent?.discountPercent ?? 10,
                        dispatchBonusKm: da.levels?.excellent?.dispatchBonusKm ?? 5,
                        levelUpBonus: da.levels?.excellent?.levelUpBonus ?? 0,
                    },
                },
            });
            const bd = settings?.biddingDisplay || {};
            setBiddingDisplayConfig({
                firstBidWindowSeconds: bd.firstBidWindowSeconds ?? 8,
                // Each side falls back to the old single "±" value until it's saved.
                randomOffsetMaxUp: bd.randomOffsetMaxUp ?? bd.randomOffsetMax ?? 15,
                randomOffsetMaxDown: bd.randomOffsetMaxDown ?? bd.randomOffsetMax ?? 15,
                randomOffsetStep: bd.randomOffsetStep ?? 5,
            });
            const ppt = settings?.payPerTrip || {};
            setPayPerTripConfig({
                enabled: Boolean(ppt.enabled),
                pricePerTrip: ppt.pricePerTrip ?? 10,
                maxDebit: ppt.maxDebit ?? 50,
            });
            const cb = settings?.cancellationBan || {};
            setCancellationBanConfig({
                enabled: cb.enabled !== false,
                maxMonthlyCancellations: cb.maxMonthlyCancellations ?? 3,
                banDurationDays: cb.banDurationDays ?? 7,
                forgiveAfterCompletedTrips: cb.forgiveAfterCompletedTrips ?? 15,
                userCancellationThreshold: cb.userCancellationThreshold ?? 3,
            });
        }
    }, [settings]);

    // Loads what the app shows right now (saved lists, or the defaults if never saved).
    const loadPolicyDraft = async () => {
        setPolicyLoading(true);
        try {
            const base = process.env.REACT_APP_API_BASE_URL;
            const [u, d] = await Promise.all(['user', 'driver'].map((role) => fetch(`${base}/api/app/policy?role=${role}`).then((r) => r.json())));
            setPolicyDraft({ user: u?.points || [], driver: d?.points || [] });
        } catch (_err) {
            setPolicyDraft({ user: settings?.jaheezPolicy?.user || [], driver: settings?.jaheezPolicy?.driver || [] });
        } finally {
            setPolicyLoading(false);
        }
    };

    const openModal = (modalName) => {
        // Start from the saved links (a cancelled edit doesn't stick).
        if (modalName === 'socialLinks') setSocialLinks({ ...EMPTY_SOCIALS, ...(settings?.socialLinks || {}) });
        if (modalName === 'jaheezPolicy') {
            setPolicyTab('user');
            loadPolicyDraft();
        }
        setActiveModal(modalName);
    };

    const updatePolicy = (fn) => setPolicyDraft((prev) => ({ ...prev, [policyTab]: fn(prev[policyTab]) }));
    const movePolicyPoint = (index, dir) => updatePolicy((list) => {
        const next = [...list];
        const target = index + dir;
        if (target < 0 || target >= next.length) return list;
        [next[index], next[target]] = [next[target], next[index]];
        return next;
    });

    const handleSaveJaheezPolicy = async () => {
        try {
            setModalLoading(true);
            const clean = (list) => list.map((p) => String(p || '').trim()).filter(Boolean);
            await updateJaheezPolicy({ user: clean(policyDraft.user), driver: clean(policyDraft.driver) });
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setActiveModal(null);
        setPackageForm({
            _id: null,
            name: '',
            packageType: 'standard',
            price: '',
            durationInDays: '',
            maxTrips: '',
            isThereDiscount: false,
            priceAfterDiscount: '',
            packageImage: '',
            description: '',
            badgeLabel: '',
            statusText: '',
            statusTone: 'info',
            ctaText: 'اشتراك الان',
            isActive: true,
            minLevel: 'normal'
        });
        setPackageImageFile([]);
    };

    // Price Ranges handlers
    const addPriceRange = () => {
        setPriceRanges([...priceRanges, { fromKm: 0, toKm: 0, startingPrice: 0, pricePerKm: 0 }]);
    };

    const removePriceRange = (index) => {
        setPriceRanges(priceRanges.filter((_, i) => i !== index));
    };

    const updatePriceRange = (index, field, value) => {
        const updated = [...priceRanges];
        updated[index][field] = parseFloat(value) || 0;
        setPriceRanges(updated);
    };

    const handleSavePriceRanges = async () => {
        try {
            setModalLoading(true);
            await updatePriceRanges(priceRanges);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Max Search Range handlers
    const handleSaveMaxSearchRange = async () => {
        try {
            setModalLoading(true);
            await updateMaxSearchRange(parseFloat(maxSearchRange));
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Referral Prize handlers
    const handleSaveReferralPrize = async () => {
        try {
            setModalLoading(true);
            await updateReferralPrize(parseFloat(referralPrize));
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Driver search config handlers
    const handleSaveDriverSearchConfig = async () => {
        try {
            setModalLoading(true);
            await updateDriverSearchConfig({
                initialDriverBatch: Number(driverSearchConfig.initialDriverBatch),
                nextDriverBatch: Number(driverSearchConfig.nextDriverBatch),
                nextDriverDelaySeconds: Number(driverSearchConfig.nextDriverDelaySeconds),
            });
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Driver activity config handlers
    const updateDriverActivityField = (field, value) => {
        setDriverActivityConfig((prev) => ({ ...prev, [field]: value }));
    };

    const updateDriverActivityLevelField = (levelKey, field, value) => {
        setDriverActivityConfig((prev) => ({
            ...prev,
            levels: {
                ...prev.levels,
                [levelKey]: { ...prev.levels[levelKey], [field]: value },
            },
        }));
    };

    const handleSaveDriverActivityConfig = async () => {
        try {
            setModalLoading(true);
            const numFields = [
                'noBidPenalty', 'bidHigherPoints', 'bidEqualPoints', 'bidLowerPoints',
                'tripOnTimePoints', 'tripLateWithinHourPoints', 'tripLateOverHourPoints',
                'tripCancelPenalty', 'expectedTimePerKmMinutes', 'expectedTimeBaseMinutes',
            ];
            const payload = {};
            numFields.forEach((field) => {
                payload[field] = Number(driverActivityConfig[field]);
            });
            payload.levels = {};
            ['normal', 'good', 'excellent'].forEach((levelKey) => {
                payload.levels[levelKey] = {
                    minPoints: Number(driverActivityConfig.levels[levelKey].minPoints),
                    discountPercent: Number(driverActivityConfig.levels[levelKey].discountPercent),
                    dispatchBonusKm: Number(driverActivityConfig.levels[levelKey].dispatchBonusKm),
                    levelUpBonus: Number(driverActivityConfig.levels[levelKey].levelUpBonus),
                };
            });
            await updateDriverActivityConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Bidding display config handlers
    const updateBiddingDisplayField = (field, value) => {
        setBiddingDisplayConfig((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveBiddingDisplayConfig = async () => {
        try {
            setModalLoading(true);
            const payload = {
                firstBidWindowSeconds: Number(biddingDisplayConfig.firstBidWindowSeconds),
                randomOffsetMaxUp: Number(biddingDisplayConfig.randomOffsetMaxUp),
                randomOffsetMaxDown: Number(biddingDisplayConfig.randomOffsetMaxDown),
                randomOffsetStep: Number(biddingDisplayConfig.randomOffsetStep),
            };
            await updateBiddingDisplayConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Pay-per-trip config handlers
    const updatePayPerTripField = (field, value) => {
        setPayPerTripConfig((prev) => ({ ...prev, [field]: value }));
    };

    const handleSavePayPerTripConfig = async () => {
        try {
            setModalLoading(true);
            const payload = {
                enabled: Boolean(payPerTripConfig.enabled),
                pricePerTrip: Number(payPerTripConfig.pricePerTrip),
                maxDebit: Number(payPerTripConfig.maxDebit),
            };
            await updatePayPerTripConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Cancellation-ban config handlers
    const updateCancellationBanField = (field, value) => {
        setCancellationBanConfig((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveCancellationBanConfig = async () => {
        try {
            setModalLoading(true);
            const payload = {
                enabled: Boolean(cancellationBanConfig.enabled),
                maxMonthlyCancellations: Number(cancellationBanConfig.maxMonthlyCancellations),
                banDurationDays: Number(cancellationBanConfig.banDurationDays),
                forgiveAfterCompletedTrips: Math.max(0, Math.floor(Number(cancellationBanConfig.forgiveAfterCompletedTrips) || 0)),
                userCancellationThreshold: Math.max(0, Math.floor(Number(cancellationBanConfig.userCancellationThreshold) || 0)),
            };
            await updateCancellationBanConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Joining prize handlers
    const handleSaveJoiningPrize = async () => {
        try {
            setModalLoading(true);
            await updateJoiningPrize(Boolean(joiningPrizeEnabled));
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const addEmergencyNumber = () => {
        setEmergencyNumbers([...emergencyNumbers, { name: '', phoneNumber: '' }]);
    };

    const updateEmergencyNumber = (index, field, value) => {
        const updated = [...emergencyNumbers];
        updated[index][field] = value;
        setEmergencyNumbers(updated);
    };

    const removeEmergencyNumber = (index) => {
        setEmergencyNumbers(emergencyNumbers.filter((_, i) => i !== index));
    };

    const handleSaveEmergencyNumbers = async () => {
        try {
            setModalLoading(true);
            const cleaned = emergencyNumbers
                .map((item) => ({
                    name: (item.name || '').trim(),
                    phoneNumber: (item.phoneNumber || '').trim(),
                }))
                .filter((item) => item.name && item.phoneNumber);

            await updateEmergencyNumbers(cleaned);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const handleSaveSocialLinks = async () => {
        try {
            setModalLoading(true);
            const payload = Object.fromEntries(Object.keys(EMPTY_SOCIALS).map((key) => [key, String(socialLinks[key] || '').trim()]));
            await updateSocialLinks(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const updateAppUpdateField = (platform, field, value) => {
        setAppUpdateConfig((prev) => ({
            ...prev,
            [platform]: {
                ...(prev[platform] || {}),
                [field]: value,
            },
        }));
    };

    const handleSaveAppUpdateConfig = async () => {
        try {
            setModalLoading(true);
            const payload = {
                forceUpdate: Boolean(appUpdateConfig.forceUpdate),
                message: (appUpdateConfig.message || '').trim() || 'يوجد تحديث إجباري للتطبيق. يرجى التحديث للمتابعة.',
                android: {
                    minimumVersion: (appUpdateConfig.android?.minimumVersion || '').trim(),
                    minimumBuild: (appUpdateConfig.android?.minimumBuild || '').trim(),
                    storeUrl: (appUpdateConfig.android?.storeUrl || '').trim(),
                },
                ios: {
                    minimumVersion: (appUpdateConfig.ios?.minimumVersion || '').trim(),
                    minimumBuild: (appUpdateConfig.ios?.minimumBuild || '').trim(),
                    storeUrl: (appUpdateConfig.ios?.storeUrl || '').trim(),
                },
            };

            await updateAppUpdateConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const handleSaveAppTermsConfig = async () => {
        try {
            setModalLoading(true);
            const payload = {
                userTerms: (appTermsConfig.userTerms || '').trim() || DEFAULT_SIGNUP_TERMS,
                driverTerms: (appTermsConfig.driverTerms || '').trim() || DEFAULT_SIGNUP_TERMS,
            };

            await updateAppTermsConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Package handlers
    const handleEditPackage = (pkg) => {
        setPackageForm({
            _id: pkg._id,
            name: pkg.name,
            packageType: pkg.packageType || 'standard',
            price: pkg.price,
            durationInDays: pkg.durationInDays,
            maxTrips: pkg.maxTrips || '',
            isThereDiscount: pkg.isThereDiscount,
            priceAfterDiscount: pkg.priceAfterDiscount || '',
            packageImage: pkg.packageImage,
            description: Array.isArray(pkg.description) ? pkg.description.join('\n') : '',
            badgeLabel: pkg.badgeLabel || '',
            statusText: pkg.statusText || '',
            statusTone: pkg.statusTone || 'info',
            ctaText: pkg.ctaText || 'اشتراك الان',
            isActive: pkg.isActive !== undefined ? pkg.isActive : true,
            minLevel: pkg.minLevel || 'normal'
        });
        openModal('packages');
    };

    const handleDeletePackage = async (packageId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه الباقة؟')) {
            return;
        }
        try {
            await deletePackage(packageId);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSavePackage = async () => {
        try {
            setModalLoading(true);
            
            // Use the uploaded image if available, otherwise use the existing URL
            let imageToSave = packageForm.packageImage;
            if (packageImageFile.length > 0) {
                imageToSave = packageImageFile[0];
            }
            
            const packageData = {
                name: packageForm.name,
                packageType: packageForm.packageType,
                price: parseFloat(packageForm.price),
                durationInDays: parseInt(packageForm.durationInDays),
                maxTrips: packageForm.maxTrips === '' ? null : parseInt(packageForm.maxTrips),
                isThereDiscount: packageForm.isThereDiscount,
                priceAfterDiscount: packageForm.isThereDiscount ? parseFloat(packageForm.priceAfterDiscount) : undefined,
                packageImage: imageToSave,
                description: (packageForm.description || '').split('\n').map(t => t.trim()).filter(Boolean),
                badgeLabel: packageForm.badgeLabel || undefined,
                statusText: packageForm.statusText || undefined,
                statusTone: packageForm.statusTone || undefined,
                ctaText: packageForm.ctaText || undefined,
                isActive: packageForm.isActive,
                minLevel: packageForm.minLevel || 'normal'
            };

            if (packageForm._id) {
                await updatePackage(packageForm._id, packageData);
            } else {
                await createPackage(packageData);
            }
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    if (isLoading) {
        return (
            <section className='container settings'>
                <div className="loading">جاري التحميل...</div>
            </section>
        );
    }

    return (
        <section className='container settings'>
            <h1 className="TXT-heading color-normal">الإعدادات</h1>
            <p className="TXT-normal color-light">إدارة إعدادات التطبيق</p>

            <div className="settings-grid">
                {/* Price Ranges Card */}
                <div className="setting-card" onClick={() => openModal('priceRanges')}>
                    <div className="card-icon">
                        <FaMoneyBillWave />
                    </div>
                    <h3>حساب سعر الرحلة</h3>
                    <p>إدارة نطاقات الأسعار حسب المسافة</p>
                    <div className="card-value">
                        {priceRanges.length} نطاق
                    </div>
                </div>

                {/* Max Search Range Card */}
                <div className="setting-card" onClick={() => openModal('maxSearchRange')}>
                    <div className="card-icon">
                        <FaMapMarkerAlt />
                    </div>
                    <h3>المسافة القصوى للبحث</h3>
                    <p>أقصى مسافة يمكن للمستخدم البحث فيها عن سائق</p>
                    <div className="card-value">
                        {maxSearchRange} كم
                    </div>
                </div>

                {/* Driver referrals now give "نقاطي" points (no more cash prize in the balance) */}
                <Link to="/points" className="setting-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="card-icon">
                        <FaGift />
                    </div>
                    <h3>دعوة السائقين</h3>
                    <p>أصبحت مكافأة دعوة سائق نقاطاً بدل الرصيد، وتُدار من صفحة النقاط ← السائقين</p>
                    <div className="card-value">إدارة ←</div>
                </Link>

                {/* Joining Prize Card */}
                <div className="setting-card" onClick={() => openModal('joiningPrize')}>
                    <div className="card-icon">
                        <FaHandshake />
                    </div>
                    <h3>جائزة الانضمام</h3>
                    <p>منح أول اشتراك مجاني عند الرصيد الكافي</p>
                    <div className="card-value">
                        {joiningPrizeEnabled ? 'مفعّلة' : 'غير مفعّلة'}
                    </div>
                </div>

                {/* Emergency Numbers Card */}
                <div className="setting-card" onClick={() => openModal('emergencyNumbers')}>
                    <div className="card-icon">
                        <FaPhoneAlt />
                    </div>
                    <h3>أرقام الطوارئ</h3>
                    <p>إدارة قائمة التواصل العاجل</p>
                    <div className="card-value">
                        {emergencyCount} رقم
                    </div>
                </div>

                {/* Points program (managed on its own page) */}
                <Link to="/points" className="setting-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="card-icon">
                        <FaStar />
                    </div>
                    <h3>برنامج النقاط</h3>
                    <p>قيمة النقطة، المهام ونقاطها، والمراجعة</p>
                    <div className="card-value">إدارة ←</div>
                </Link>

                {/* Users' smart assistant (managed on the Smart Assistant page) */}
                <Link to="/smart-assistant?tab=users" className="setting-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="card-icon">
                        <FaRobot />
                    </div>
                    <h3>المساعد الذكي للمستخدمين</h3>
                    <p>الأسئلة الشائعة، معلومات التطبيق، أرقام الدعم، وحدود الاستخدام اليومية</p>
                    <div className="card-value">
                        {settings?.userAssistant?.enabled === false
                            ? 'متوقف'
                            : `مفعّل · ${settings?.userAssistant?.dailyAiMessagesPerUser ?? 30} سؤال يومياً`}
                    </div>
                </Link>

                {/* Social Links Card */}
                <div className="setting-card" onClick={() => openModal('socialLinks')}>
                    <div className="card-icon">
                        <FaShareAlt />
                    </div>
                    <h3>روابط التواصل الاجتماعي</h3>
                    <p>تظهر في القوائم الجانبية للتطبيق</p>
                    <div className="card-value">
                        {socialCount} من {SOCIAL_FIELDS.length}
                    </div>
                </div>

                {/* Packages Card */}
                <div className="setting-card" onClick={() => openModal('packages')}>
                    <div className="card-icon">
                        <FaBox />
                    </div>
                    <h3>الباقات</h3>
                    <p>إدارة باقات الاشتراك</p>
                    <div className="card-value">
                        {packages.length} باقة
                    </div>
                </div>

                {/* Driver Search Config Card */}
                <div className="setting-card" onClick={() => openModal('driverSearchConfig')}>
                    <div className="card-icon">
                        <FaTools />
                    </div>
                    <h3>إعدادات البحث عن السائقين</h3>
                    <p>عدد السائقين في كل دفعة إشعار والوقت بينها</p>
                    <div className="card-value">
                        {driverSearchConfig.initialDriverBatch} ← {driverSearchConfig.nextDriverBatch} كل {driverSearchConfig.nextDriverDelaySeconds}ث
                    </div>
                </div>

                {/* Driver Activity Config Card */}
                <div className="setting-card" onClick={() => openModal('driverActivityConfig')}>
                    <div className="card-icon">
                        <FaStar />
                    </div>
                    <h3>نقاط نشاط السائقين</h3>
                    <p>إدارة نقاط الأداء والمستويات والخصومات والأولوية</p>
                    <div className="card-value">
                        عادي {driverActivityConfig.levels.normal.minPoints}+ / جيد {driverActivityConfig.levels.good.minPoints}+ / ممتاز {driverActivityConfig.levels.excellent.minPoints}+
                    </div>
                </div>

                {/* Bidding Display Config Card */}
                <div className="setting-card" onClick={() => openModal('biddingDisplayConfig')}>
                    <div className="card-icon">
                        <FaChartLine />
                    </div>
                    <h3>متوسط المزايدة المعروض للسائق</h3>
                    <p>تحكم في مدة "كن أول من يزايد" ونطاق الرقم العشوائي المعروض قبل ورود عروض حقيقية</p>
                    <div className="card-value">
                        {biddingDisplayConfig.firstBidWindowSeconds} ثانية / +{biddingDisplayConfig.randomOffsetMaxUp} −{biddingDisplayConfig.randomOffsetMaxDown}
                    </div>
                </div>

                {/* Pay-Per-Trip Config Card */}
                <div className="setting-card" onClick={() => openModal('payPerTripConfig')}>
                    <div className="card-icon">
                        <FaWallet />
                    </div>
                    <h3>الدفع لكل رحلة (بدون اشتراك)</h3>
                    <p>السماح للسائقين بدون اشتراك بأخذ رحلات مقابل خصم من رصيدهم، مع حد أقصى للرصيد السالب</p>
                    <div className="card-value">
                        {payPerTripConfig.enabled ? 'مفعّل' : 'متوقف'} - {payPerTripConfig.pricePerTrip} د.ل / رحلة
                    </div>
                </div>

                {/* Cancellation Ban Config Card */}
                <div className="setting-card" onClick={() => openModal('cancellationBanConfig')}>
                    <div className="card-icon">
                        <FaBan />
                    </div>
                    <h3>إيقاف الإلغاءات المتكررة</h3>
                    <p>إيقاف السائق مؤقتاً إذا تجاوز الحد المسموح به لإلغاء الرحلات خلال 30 يوماً</p>
                    <div className="card-value">
                        {cancellationBanConfig.enabled ? 'مفعّل' : 'متوقف'} - {cancellationBanConfig.maxMonthlyCancellations} إلغاءات / {cancellationBanConfig.banDurationDays} أيام
                        {Number(cancellationBanConfig.forgiveAfterCompletedTrips) > 0 ? ` · مسح بعد ${cancellationBanConfig.forgiveAfterCompletedTrips} رحلة` : ''}
                    </div>
                </div>

                <div className="setting-card" onClick={() => openModal('appUpdate')}>
                    <div className="card-icon">
                        <FaCloudDownloadAlt />
                    </div>
                    <h3>التحديث الإجباري</h3>
                    <p>إدارة شروط ورسالة التحديث ومتاجر التحميل</p>
                    <div className="card-value">
                        {appUpdateConfig.forceUpdate ? 'مفعّل' : 'متوقف'}
                    </div>
                </div>

                <div className="setting-card" onClick={() => openModal('appTerms')}>
                    <div className="card-icon">
                        <FaEdit />
                    </div>
                    <h3>شروط التسجيل</h3>
                    <p>إدارة النصوص المعروضة للمستخدم والسائق</p>
                    <div className="card-value">
                        مفصولة حسب الدور
                    </div>
                </div>

                {/* "سياسة جاهز" pages in the app (users / drivers, separate lists) */}
                <div className="setting-card" onClick={() => openModal('jaheezPolicy')}>
                    <div className="card-icon">
                        <FaShieldAlt />
                    </div>
                    <h3>سياسة جاهز</h3>
                    <p>نقاط صفحة السياسة في تطبيق المستخدمين والسائقين</p>
                    <div className="card-value">
                        {Array.isArray(settings?.jaheezPolicy?.user)
                            ? `المستخدمين ${settings.jaheezPolicy.user.length} · السائقين ${settings.jaheezPolicy.driver?.length || 0}`
                            : 'النقاط الافتراضية'}
                    </div>
                </div>
            </div>

            {activeModal === 'driverSearchConfig' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>إعدادات البحث عن السائقين</h2>
                        <p className="modal-hint">عند إنشاء طلب جديد، يتم إشعار أقرب X سائق فوراً، ثم إشعار Y سائق كل Z ثانية حتى يتم تغطية جميع السائقين في النطاق.</p>

                        <div className="input-group">
                            <label>عدد السائقين في الدفعة الأولى</label>
                            <input
                                type="number"
                                min="1"
                                value={driverSearchConfig.initialDriverBatch}
                                onChange={(e) => setDriverSearchConfig((prev) => ({ ...prev, initialDriverBatch: e.target.value }))}
                                placeholder="مثال: 15"
                            />
                        </div>

                        <div className="input-group">
                            <label>عدد السائقين في كل دفعة تالية</label>
                            <input
                                type="number"
                                min="1"
                                value={driverSearchConfig.nextDriverBatch}
                                onChange={(e) => setDriverSearchConfig((prev) => ({ ...prev, nextDriverBatch: e.target.value }))}
                                placeholder="مثال: 5"
                            />
                        </div>

                        <div className="input-group">
                            <label>وقت الانتظار بين الدفعات (بالثواني)</label>
                            <input
                                type="number"
                                min="1"
                                value={driverSearchConfig.nextDriverDelaySeconds}
                                onChange={(e) => setDriverSearchConfig((prev) => ({ ...prev, nextDriverDelaySeconds: e.target.value }))}
                                placeholder="مثال: 20"
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveDriverSearchConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'driverActivityConfig' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>نقاط نشاط السائقين</h2>
                        <p className="modal-hint">تحكم في نقاط الأداء، صيغة الوقت المتوقع للرحلة، وحدود المستويات وما يترتب عليها من خصم على الاشتراك وأولوية في إشعارات الطلبات.</p>

                        <h3 style={{ marginTop: 10 }}>نقاط العروض (البيدات)</h3>
                        <div className="input-row">
                            <div className="input-group">
                                <label>عدم تقديم عرض بعد الإشعار</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.noBidPenalty}
                                    onChange={(e) => updateDriverActivityField('noBidPenalty', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>عرض أعلى من السعر الابتدائي</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.bidHigherPoints}
                                    onChange={(e) => updateDriverActivityField('bidHigherPoints', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="input-row">
                            <div className="input-group">
                                <label>عرض يساوي السعر الابتدائي</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.bidEqualPoints}
                                    onChange={(e) => updateDriverActivityField('bidEqualPoints', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>عرض أقل من السعر الابتدائي</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.bidLowerPoints}
                                    onChange={(e) => updateDriverActivityField('bidLowerPoints', e.target.value)}
                                />
                            </div>
                        </div>

                        <h3 style={{ marginTop: 14 }}>الوقت المتوقع للرحلة</h3>
                        <p className="modal-hint">الوقت المتوقع (دقيقة) = المسافة (كم) × المعامل + الوقت الأساسي</p>
                        <div className="input-row">
                            <div className="input-group">
                                <label>معامل الدقائق لكل كم</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={driverActivityConfig.expectedTimePerKmMinutes}
                                    onChange={(e) => updateDriverActivityField('expectedTimePerKmMinutes', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>الوقت الأساسي (دقيقة)</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.expectedTimeBaseMinutes}
                                    onChange={(e) => updateDriverActivityField('expectedTimeBaseMinutes', e.target.value)}
                                />
                            </div>
                        </div>

                        <h3 style={{ marginTop: 14 }}>نقاط إتمام الرحلة</h3>
                        <div className="input-row">
                            <div className="input-group">
                                <label>ضمن الوقت المتوقع</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.tripOnTimePoints}
                                    onChange={(e) => updateDriverActivityField('tripOnTimePoints', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>متأخر حتى ساعة</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.tripLateWithinHourPoints}
                                    onChange={(e) => updateDriverActivityField('tripLateWithinHourPoints', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>متأخر أكثر من ساعة</label>
                                <input
                                    type="number"
                                    value={driverActivityConfig.tripLateOverHourPoints}
                                    onChange={(e) => updateDriverActivityField('tripLateOverHourPoints', e.target.value)}
                                />
                            </div>
                        </div>

                        <h3 style={{ marginTop: 14 }}>إلغاء الرحلة من طرف السائق</h3>
                        <div className="input-group">
                            <label>عقوبة الإلغاء</label>
                            <input
                                type="number"
                                value={driverActivityConfig.tripCancelPenalty}
                                onChange={(e) => updateDriverActivityField('tripCancelPenalty', e.target.value)}
                            />
                        </div>

                        <h3 style={{ marginTop: 14 }}>المستويات (الحد الأدنى للنقاط، نسبة الخصم، وأولوية الإشعار)</h3>
                        <p className="modal-hint">مستوى "ضعيف" هو كل ما هو أقل من الحد الأدنى لمستوى "عادي".</p>
                        {['normal', 'good', 'excellent'].map((levelKey) => (
                            <div key={levelKey} className="input-row">
                                <div className="input-group">
                                    <label>
                                        {levelKey === 'normal' ? 'الحد الأدنى - عادي' : levelKey === 'good' ? 'الحد الأدنى - جيد' : 'الحد الأدنى - ممتاز'}
                                    </label>
                                    <input
                                        type="number"
                                        value={driverActivityConfig.levels[levelKey].minPoints}
                                        onChange={(e) => updateDriverActivityLevelField(levelKey, 'minPoints', e.target.value)}
                                    />
                                </div>
                                <div className="input-group">
                                    <label>نسبة خصم الاشتراك (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={driverActivityConfig.levels[levelKey].discountPercent}
                                        onChange={(e) => updateDriverActivityLevelField(levelKey, 'discountPercent', e.target.value)}
                                    />
                                </div>
                                <div className="input-group">
                                    <label>أولوية الإشعار (كم إضافية)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={driverActivityConfig.levels[levelKey].dispatchBonusKm}
                                        onChange={(e) => updateDriverActivityLevelField(levelKey, 'dispatchBonusKm', e.target.value)}
                                    />
                                </div>
                                <div className="input-group">
                                    <label>مكافأة الترقية (د.ل)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={driverActivityConfig.levels[levelKey].levelUpBonus}
                                        onChange={(e) => updateDriverActivityLevelField(levelKey, 'levelUpBonus', e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveDriverActivityConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'biddingDisplayConfig' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>متوسط المزايدة المعروض للسائق</h2>
                        <p className="modal-hint">
                            في أول عدد من الثواني بعد إنشاء الطلب يظهر للسائق "كن أول من يزايد". بعد ذلك، إذا لم يوجد عرض حقيقي بعد، يُعرض رقم عشوائي ثابت لهذا الطلب (السعر الأساسي ± القيمة المحددة أدناه بخطوات الفارق). بمجرد ورود عروض حقيقية، يُعرض دائماً أقل قيمة بين هذا الرقم العشوائي وأقل عرض حقيقي.
                        </p>

                        <div className="input-group">
                            <label>مدة "كن أول من يزايد" (ثانية)</label>
                            <input
                                type="number"
                                min="0"
                                value={biddingDisplayConfig.firstBidWindowSeconds}
                                onChange={(e) => updateBiddingDisplayField('firstBidWindowSeconds', e.target.value)}
                            />
                        </div>

                        <div className="input-row">
                            <div className="input-group">
                                <label>أقصى فارق موجب (+ د.ل)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={biddingDisplayConfig.randomOffsetMaxUp}
                                    onChange={(e) => updateBiddingDisplayField('randomOffsetMaxUp', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>أقصى فارق سالب (− د.ل)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={biddingDisplayConfig.randomOffsetMaxDown}
                                    onChange={(e) => updateBiddingDisplayField('randomOffsetMaxDown', e.target.value)}
                                />
                            </div>
                        </div>
                        <p className="modal-hint">
                            {`المتوسط المعروض يكون بين السعر التقديري − ${Number(biddingDisplayConfig.randomOffsetMaxDown) || 0} و + ${Number(biddingDisplayConfig.randomOffsetMaxUp) || 0} د.ل. ضع 0 في الفارق السالب حتى لا يقل عن السعر التقديري أبداً.`}
                        </p>

                        <div className="input-row">
                            <div className="input-group">
                                <label>خطوة الفارق (د.ل)</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={biddingDisplayConfig.randomOffsetStep}
                                    onChange={(e) => updateBiddingDisplayField('randomOffsetStep', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveBiddingDisplayConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'payPerTripConfig' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>الدفع لكل رحلة (بدون اشتراك)</h2>
                        <p className="modal-hint">
                            عند التفعيل: السائق الذي ليس لديه اشتراك ساري يبقى نشطاً ويستقبل الطلبات، ويُخصم منه سعر الرحلة أدناه من رصيده عند إكمال كل رحلة (يمكن أن يصبح الرصيد سالباً). بمجرد أن يتجاوز رصيده السالب الحد الأقصى المحدد، يصبح غير نشط تلقائياً حتى يشحن رصيده أو يشترك.
                        </p>

                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={Boolean(payPerTripConfig.enabled)}
                                    onChange={(e) => updatePayPerTripField('enabled', e.target.checked)}
                                />
                                تفعيل الدفع لكل رحلة
                            </label>
                        </div>

                        <div className="input-row">
                            <div className="input-group">
                                <label>سعر كل رحلة (د.ل)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={payPerTripConfig.pricePerTrip}
                                    onChange={(e) => updatePayPerTripField('pricePerTrip', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>الحد الأقصى للرصيد السالب (د.ل)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={payPerTripConfig.maxDebit}
                                    onChange={(e) => updatePayPerTripField('maxDebit', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSavePayPerTripConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'cancellationBanConfig' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>إيقاف الإلغاءات المتكررة</h2>
                        <p className="modal-hint">
                            عند التفعيل: إذا ألغى السائق عدد الرحلات المحدد أدناه خلال آخر 30 يوماً، يتم إيقافه مؤقتاً عن استقبال طلبات جديدة للمدة المحددة، مع إشعاره بذلك. لا يتم تسجيل الخروج من حسابه ويستمر في تلقي الإشعارات العامة.
                        </p>

                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={Boolean(cancellationBanConfig.enabled)}
                                    onChange={(e) => updateCancellationBanField('enabled', e.target.checked)}
                                />
                                تفعيل إيقاف الإلغاءات المتكررة
                            </label>
                        </div>

                        <div className="input-row">
                            <div className="input-group">
                                <label>الحد الأقصى للإلغاءات شهرياً</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={cancellationBanConfig.maxMonthlyCancellations}
                                    onChange={(e) => updateCancellationBanField('maxMonthlyCancellations', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>مدة الإيقاف (أيام)</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={cancellationBanConfig.banDurationDays}
                                    onChange={(e) => updateCancellationBanField('banDurationDays', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="input-row">
                            <div className="input-group">
                                <label>عدد الرحلات المكتملة لمسح الإلغاءات</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={cancellationBanConfig.forgiveAfterCompletedTrips}
                                    onChange={(e) => updateCancellationBanField('forgiveAfterCompletedTrips', e.target.value)}
                                />
                            </div>
                            <div className="input-group">
                                <label>حد إلغاءات المستخدمين (حالات الباند)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={cancellationBanConfig.userCancellationThreshold}
                                    onChange={(e) => updateCancellationBanField('userCancellationThreshold', e.target.value)}
                                />
                            </div>
                        </div>
                        <p className="modal-hint">
                            مسح الإلغاءات: إذا أكمل السائق هذا العدد من الرحلات منذ تاريخ أول إلغاء مسجّل، تُمسح إلغاءاته (التي ألغاها هو والتي ألغاها المستخدمون) ويبدأ من الصفر، وتبقى الرحلات ظاهرة في صفحة الرحلات. 0 لإيقاف المسح.
                            <br />
                            حد إلغاءات المستخدمين: عندما يلغي المستخدمون هذا العدد من رحلات السائق خلال 30 يوماً يظهر في صفحة "حالات الباند" (دون إيقافه تلقائياً). 0 للإيقاف.
                        </p>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveCancellationBanConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'appUpdate' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>إعدادات التحديث الإجباري</h2>
                        <p className="modal-hint">عند تفعيل الخيار: سيتم تطبيق الحد الأدنى للنسخة/البيلد لكل منصة. إذا كانت الحقول فارغة سيتم فرض التحديث على الجميع.</p>

                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={Boolean(appUpdateConfig.forceUpdate)}
                                    onChange={(e) => setAppUpdateConfig((prev) => ({ ...prev, forceUpdate: e.target.checked }))}
                                />
                                تفعيل التحديث الإجباري
                            </label>
                        </div>

                        <div className="input-group">
                            <label>رسالة التحديث</label>
                            <textarea
                                rows={3}
                                value={appUpdateConfig.message}
                                onChange={(e) => setAppUpdateConfig((prev) => ({ ...prev, message: e.target.value }))}
                            />
                        </div>

                        <h3 style={{ marginTop: 10 }}>Android</h3>
                        <div className="input-row">
                            <div className="input-group">
                                <label>أدنى نسخة</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.android.minimumVersion}
                                    onChange={(e) => updateAppUpdateField('android', 'minimumVersion', e.target.value)}
                                    placeholder="مثال: 1.0.2"
                                />
                            </div>
                            <div className="input-group">
                                <label>أدنى Build</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.android.minimumBuild}
                                    onChange={(e) => updateAppUpdateField('android', 'minimumBuild', e.target.value)}
                                    placeholder="مثال: 28"
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label>رابط المتجر (Android)</label>
                            <input
                                type="text"
                                value={appUpdateConfig.android.storeUrl}
                                onChange={(e) => updateAppUpdateField('android', 'storeUrl', e.target.value)}
                                placeholder="https://play.google.com/store/apps/details?id=com.jahez.ly"
                            />
                        </div>

                        <h3 style={{ marginTop: 10 }}>iOS</h3>
                        <div className="input-row">
                            <div className="input-group">
                                <label>أدنى نسخة</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.ios.minimumVersion}
                                    onChange={(e) => updateAppUpdateField('ios', 'minimumVersion', e.target.value)}
                                    placeholder="مثال: 1.0.3"
                                />
                            </div>
                            <div className="input-group">
                                <label>أدنى Build</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.ios.minimumBuild}
                                    onChange={(e) => updateAppUpdateField('ios', 'minimumBuild', e.target.value)}
                                    placeholder="مثال: 29"
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label>رابط المتجر (iOS)</label>
                            <input
                                type="text"
                                value={appUpdateConfig.ios.storeUrl}
                                onChange={(e) => updateAppUpdateField('ios', 'storeUrl', e.target.value)}
                                placeholder="https://apps.apple.com/..."
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveAppUpdateConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'appTerms' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>شروط التسجيل (مستخدم / سائق)</h2>
                        <p className="modal-hint">هذه النصوص تظهر داخل نافذة الموافقة على الشروط في شاشة إنشاء الحساب.</p>

                        <div className="input-group">
                            <label>شروط المستخدم</label>
                            <textarea
                                rows={10}
                                value={appTermsConfig.userTerms}
                                onChange={(e) => setAppTermsConfig((prev) => ({ ...prev, userTerms: e.target.value }))}
                            />
                        </div>

                        <div className="input-group">
                            <label>شروط السائق</label>
                            <textarea
                                rows={10}
                                value={appTermsConfig.driverTerms}
                                onChange={(e) => setAppTermsConfig((prev) => ({ ...prev, driverTerms: e.target.value }))}
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveAppTermsConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'jaheezPolicy' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content policy-editor" onClick={(e) => e.stopPropagation()}>
                        <h2>سياسة جاهز</h2>
                        <p className="modal-hint">تظهر هذه النقاط في صفحة "سياسة جاهز" داخل التطبيق. نقاط المستخدمين منفصلة عن نقاط السائقين.</p>

                        <div className="policy-tabs">
                            {[{ key: 'user', label: 'المستخدمين' }, { key: 'driver', label: 'السائقين' }].map((t) => (
                                <button type="button" key={t.key} className={policyTab === t.key ? 'active' : ''} onClick={() => setPolicyTab(t.key)}>
                                    {t.label} <span className="count">{policyDraft[t.key].length}</span>
                                </button>
                            ))}
                        </div>

                        {policyLoading ? (
                            <div className="loading">جاري التحميل...</div>
                        ) : (
                            <div className="policy-list">
                                {policyDraft[policyTab].length === 0 ? (
                                    <div className="policy-empty">لا توجد نقاط. أضف أول نقطة.</div>
                                ) : policyDraft[policyTab].map((point, index) => (
                                    <div className="policy-point" key={`${policyTab}-${index}`}>
                                        <span className="policy-number">{index + 1}</span>
                                        <textarea
                                            rows={2}
                                            maxLength={500}
                                            value={point}
                                            placeholder="اكتب النقطة..."
                                            onChange={(e) => updatePolicy((list) => list.map((p, i) => (i === index ? e.target.value : p)))}
                                        />
                                        <div className="policy-actions">
                                            <button type="button" title="لأعلى" disabled={index === 0} onClick={() => movePolicyPoint(index, -1)}><FaArrowUp /></button>
                                            <button type="button" title="لأسفل" disabled={index === policyDraft[policyTab].length - 1} onClick={() => movePolicyPoint(index, 1)}><FaArrowDown /></button>
                                            <button type="button" title="حذف" className="danger" onClick={() => updatePolicy((list) => list.filter((_, i) => i !== index))}><FaTimes /></button>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="policy-add"
                                    disabled={policyDraft[policyTab].length >= 50}
                                    onClick={() => updatePolicy((list) => [...list, ''])}
                                >
                                    <FaPlus /> إضافة نقطة
                                </button>
                            </div>
                        )}

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveJaheezPolicy} disabled={modalLoading || policyLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Price Ranges Modal */}
            {activeModal === 'priceRanges' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>نطاقات الأسعار</h2>
                        <div className="price-ranges-list">
                            {priceRanges.map((range, index) => (
                                <div key={index} className="price-range-item">
                                    <div className="range-inputs">
                                        <div className="input-group">
                                            <label>من (كم)</label>
                                            <input
                                                type="number"
                                                value={range.fromKm}
                                                onChange={(e) => updatePriceRange(index, 'fromKm', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>إلى (كم)</label>
                                            <input
                                                type="number"
                                                value={range.toKm}
                                                onChange={(e) => updatePriceRange(index, 'toKm', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>السعر الابتدائي (د.ل)</label>
                                            <input
                                                type="number"
                                                value={range.startingPrice}
                                                onChange={(e) => updatePriceRange(index, 'startingPrice', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>السعر لكل كم (د.ل)</label>
                                            <input
                                                type="number"
                                                value={range.pricePerKm}
                                                onChange={(e) => updatePriceRange(index, 'pricePerKm', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removePriceRange(index)}
                                        disabled={priceRanges.length === 1}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button className="add-range-btn" onClick={addPriceRange}>
                            <FaPlus /> إضافة نطاق
                        </button>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSavePriceRanges} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Max Search Range Modal */}
            {activeModal === 'maxSearchRange' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>المسافة القصوى للبحث</h2>
                        <div className="input-group">
                            <label>المسافة القصوى (كم)</label>
                            <input
                                type="number"
                                value={maxSearchRange}
                                onChange={(e) => setMaxSearchRange(e.target.value)}
                                min="1"
                                placeholder="أدخل المسافة القصوى"
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveMaxSearchRange} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Referral Prize Modal */}
            {activeModal === 'referralPrize' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>جائزة الإحالة</h2>
                        <div className="input-group">
                            <label>المبلغ المكتسب (د.ل)</label>
                            <input
                                type="number"
                                value={referralPrize}
                                onChange={(e) => setReferralPrize(e.target.value)}
                                min="0"
                                placeholder="أدخل المبلغ"
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveReferralPrize} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Joining Prize Modal */}
            {activeModal === 'joiningPrize' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>جائزة الانضمام</h2>
                        <p className="modal-hint">عند تفعيلها: إذا كان رصيد السائق يغطي ثمن الباقة الأولى، يتم تفعيل الاشتراك دون خصم الرصيد لمرة واحدة فقط.</p>
                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={joiningPrizeEnabled}
                                    onChange={(e) => setJoiningPrizeEnabled(e.target.checked)}
                                />
                                تفعيل جائزة الانضمام
                            </label>
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveJoiningPrize} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Emergency Numbers Modal */}
            {activeModal === 'emergencyNumbers' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>أرقام الطوارئ</h2>
                        <p className="modal-hint">سيتم عرض هذه الأرقام في قوائم المستخدمين والسائقين.</p>
                        <div className="price-ranges-list">
                            {emergencyNumbers.map((item, index) => (
                                <div key={index} className="price-range-item">
                                    <div className="range-inputs">
                                        <div className="input-group">
                                            <label>الاسم</label>
                                            <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => updateEmergencyNumber(index, 'name', e.target.value)}
                                                placeholder="مثال: الإسعاف"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>رقم الهاتف</label>
                                            <input
                                                type="text"
                                                value={item.phoneNumber}
                                                onChange={(e) => updateEmergencyNumber(index, 'phoneNumber', e.target.value)}
                                                placeholder="09XXXXXXXX"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeEmergencyNumber(index)}
                                        disabled={emergencyNumbers.length === 1}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button className="add-range-btn" onClick={addEmergencyNumber}>
                            <FaPlus /> إضافة رقم
                        </button>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveEmergencyNumbers} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'socialLinks' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>روابط التواصل الاجتماعي</h2>
                        <p className="modal-hint">تظهر في القوائم الجانبية للمستخدمين والسائقين وأصحاب الورش. اترك الحقل فارغاً لإخفاء الأيقونة.</p>
                        {SOCIAL_FIELDS.map(({ key, label, icon: Icon, placeholder }) => (
                            <div key={key} className="input-group">
                                <label><Icon style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{label}</label>
                                <input
                                    type="text"
                                    dir="ltr"
                                    value={socialLinks[key] || ''}
                                    onChange={(e) => setSocialLinks((prev) => ({ ...prev, [key]: e.target.value }))}
                                    placeholder={placeholder}
                                />
                            </div>
                        ))}
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveSocialLinks} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Packages Modal */}
            {activeModal === 'packages' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content packages-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>إدارة الباقات</h2>
                        
                        {/* Package Form */}
                        <div className="package-form">
                            <div className="input-group">
                                <label>اسم الباقة</label>
                                <input
                                    type="text"
                                    value={packageForm.name}
                                    onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                                    placeholder="أدخل اسم الباقة"
                                />
                            </div>
                            <div className="input-group">
                                <label>نوع الباقة</label>
                                <select
                                    value={packageForm.packageType}
                                    onChange={(e) => setPackageForm({ ...packageForm, packageType: e.target.value })}
                                >
                                    <option value="standard">عادية</option>
                                    <option value="joining">باقة الانضمام</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>الحد الأدنى لمستوى النشاط</label>
                                <select
                                    value={packageForm.minLevel}
                                    onChange={(e) => setPackageForm({ ...packageForm, minLevel: e.target.value })}
                                >
                                    <option value="normal">عادي (متاحة للجميع)</option>
                                    <option value="good">جيد فأعلى</option>
                                    <option value="excellent">ممتاز فقط</option>
                                </select>
                            </div>
                            <div className="input-row">
                                <div className="input-group">
                                    <label>السعر (د.ل)</label>
                                    <input
                                        type="number"
                                        value={packageForm.price}
                                        onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                                        min="0"
                                        placeholder="السعر"
                                    />
                                </div>
                                <div className="input-group">
                                    <label>المدة (أيام)</label>
                                    <input
                                        type="number"
                                        value={packageForm.durationInDays}
                                        onChange={(e) => setPackageForm({ ...packageForm, durationInDays: e.target.value })}
                                        min="1"
                                        placeholder="المدة"
                                    />
                                </div>
                                <div className="input-group">
                                    <label>حد الرحلات المكتملة</label>
                                    <input
                                        type="number"
                                        value={packageForm.maxTrips}
                                        onChange={(e) => setPackageForm({ ...packageForm, maxTrips: e.target.value })}
                                        min="1"
                                        placeholder="اتركه فارغاً لغير المحدود"
                                    />
                                </div>
                            </div>
                            <div className="input-group">
                                <label>وصف الباقة (سطر لكل نقطة)</label>
                                <textarea
                                    value={packageForm.description}
                                    onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                                    rows={3}
                                    placeholder="أدخل وصف الباقة"
                                />
                            </div>
                            <div className="input-row">
                                <div className="input-group">
                                    <label>عنوان الشارة (اختياري)</label>
                                    <input
                                        type="text"
                                        value={packageForm.badgeLabel}
                                        onChange={(e) => setPackageForm({ ...packageForm, badgeLabel: e.target.value })}
                                        placeholder="مثال: 3"
                                    />
                                </div>
                                <div className="input-group">
                                    <label>نص الحالة</label>
                                    <input
                                        type="text"
                                        value={packageForm.statusText}
                                        onChange={(e) => setPackageForm({ ...packageForm, statusText: e.target.value })}
                                        placeholder="مثال: متاح"
                                    />
                                </div>
                            </div>
                            <div className="input-row">
                                <div className="input-group">
                                    <label>درجة الحالة</label>
                                    <select
                                        value={packageForm.statusTone}
                                        onChange={(e) => setPackageForm({ ...packageForm, statusTone: e.target.value })}
                                    >
                                        <option value="info">اعتيادي</option>
                                        <option value="success">نجاح</option>
                                        <option value="warning">تحذير</option>
                                        <option value="danger">خطر</option>
                                    </select>
                                </div>
                                <div className="input-group">
                                    <label>نص الزر</label>
                                    <input
                                        type="text"
                                        value={packageForm.ctaText}
                                        onChange={(e) => setPackageForm({ ...packageForm, ctaText: e.target.value })}
                                        placeholder="مثال: اشتراك الان"
                                    />
                                </div>
                            </div>
                            <div className="input-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={packageForm.isThereDiscount}
                                        onChange={(e) => setPackageForm({ ...packageForm, isThereDiscount: e.target.checked })}
                                    />
                                    يوجد خصم
                                </label>
                            </div>
                            <div className="input-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={packageForm.isActive}
                                        onChange={(e) => setPackageForm({ ...packageForm, isActive: e.target.checked })}
                                    />
                                    الباقة فعّالة
                                </label>
                            </div>
                            {packageForm.isThereDiscount && (
                                <div className="input-group">
                                    <label>السعر بعد الخصم (د.ل)</label>
                                    <input
                                        type="number"
                                        value={packageForm.priceAfterDiscount}
                                        onChange={(e) => setPackageForm({ ...packageForm, priceAfterDiscount: e.target.value })}
                                        min="0"
                                        placeholder="السعر بعد الخصم"
                                    />
                                </div>
                            )}
                            <div className="input-group">
                                <label>صورة الباقة</label>
                                <DropImage setAcceptedFile={setPackageImageFile} maxFiles={1}>
                                    <div className="drop-zone">
                                        {packageImageFile.length > 0 ? (
                                            <div className="image-preview">
                                                <img src={packageImageFile[0]} alt="Package preview" />
                                                <p>اضغط أو اسحب لتغيير الصورة</p>
                                            </div>
                                        ) : packageForm.packageImage ? (
                                            <div className="image-preview">
                                                <img src={packageForm.packageImage} alt="Package preview" />
                                                <p>اضغط أو اسحب لتغيير الصورة</p>
                                            </div>
                                        ) : (
                                            <div className="drop-placeholder">
                                                <FaPlus />
                                                <p>اضغط أو اسحب الصورة هنا</p>
                                                <span>PNG, JPG, GIF حتى 5MB</span>
                                            </div>
                                        )}
                                    </div>
                                </DropImage>
                            </div>
                            <button className="save-package-btn" onClick={handleSavePackage} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : (packageForm._id ? 'تحديث الباقة' : 'إضافة باقة')}
                            </button>
                        </div>

                        {/* Packages List */}
                        <div className="packages-list">
                            <h3>الباقات الحالية</h3>
                            {packages.map((pkg) => (
                                <div key={pkg._id} className="package-item">
                                    <div className="package-info">
                                        <h4>{pkg.name}</h4>
                                        <p>النوع: {pkg.packageType === 'joining' ? 'باقة الانضمام' : 'عادية'}</p>
                                        <p>مستوى النشاط المطلوب: {{ normal: 'عادي (متاحة للجميع)', good: 'جيد فأعلى', excellent: 'ممتاز فقط' }[pkg.minLevel || 'normal']}</p>
                                        <p>
                                            {pkg.isThereDiscount ? (
                                                <>
                                                    <span className="original-price">{pkg.price} د.ل</span>
                                                    <span className="discount-price">{pkg.priceAfterDiscount} د.ل</span>
                                                </>
                                            ) : (
                                                <span>{pkg.price} د.ل</span>
                                            )}
                                            {' - '}
                                            {pkg.durationInDays} يوم
                                            {' - '}
                                            {pkg.maxTrips ? `${pkg.maxTrips} رحلات` : 'رحلات غير محدودة'}
                                            {pkg.statusText ? ` - ${pkg.statusText}` : ''}
                                            {pkg.isActive === false ? ' (غير فعّالة)' : ''}
                                        </p>
                                        {pkg.badgeLabel ? <p>شارة: {pkg.badgeLabel}</p> : null}
                                        {Array.isArray(pkg.description) && pkg.description.length > 0 && (
                                            <ul className="package-desc-list">
                                                {pkg.description.map((line, idx) => (
                                                    <li key={idx}>{line}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="package-actions">
                                        <button className="edit-btn" onClick={() => handleEditPackage(pkg)}>
                                            <FaEdit />
                                        </button>
                                        <button className="delete-btn" onClick={() => handleDeletePackage(pkg._id)}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal}>
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};